import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/easyestimate-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | EasyEstimate Alternative for UK Builders",
  "description": "Looking for an EasyEstimate alternative with AI-powered quoting and a deeper UK cost library? The Proper Tool starts at £29/mo with a 14-day free trial.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "EasyEstimate Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best EasyEstimate alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK builders who want AI-powered quoting, drawing upload, and a deeper UK cost library alongside simplicity, The Proper Tool is the strongest EasyEstimate alternative. EasyEstimate is positioned as the easiest-to-start UK estimating tool for sole traders, but it has no AI, no drawing upload, and a more limited cost library. The Proper Tool starts at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Is EasyEstimate good enough for growing building businesses?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EasyEstimate is designed for sole traders who want the absolute simplest possible tool. For builders who are growing their business, taking on more complex jobs, or wanting to quote from drawings, EasyEstimate's limited feature set becomes a constraint. The Proper Tool grows with the business, adding AI scope generation, drawing analysis, scope validation, and a 519-item UK cost library.",
      },
    },
    {
      "@type": "Question",
      "name": "Can EasyEstimate read structural drawings or generate quotes from descriptions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. EasyEstimate does not read architectural drawings or generate quotes from project descriptions. All items must be entered manually. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more complete and accurate construction quote.",
      },
    },
    {
      "@type": "Question",
      "name": "How does EasyEstimate pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EasyEstimate is positioned as a low-cost option for sole traders. The Proper Tool starts at £29 per month with a 14-day free trial and no credit card required. For the additional capability, including AI quoting, drawing upload, a 519-item UK cost library, and scope validation, The Proper Tool represents strong value.",
      },
    },
    {
      "@type": "Question",
      "name": "What is the UK cost library depth difference between EasyEstimate and The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EasyEstimate has a basic cost library appropriate for simple estimating. The Proper Tool has a 519-item UK cost library across 31 residential construction trade categories, including groundworks, structural steelwork, carpentry, roofing, and finishing trades. The Pricing Accuracy Index learns from your actual margins over time, improving accuracy job by job.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  easyestimate: string;
  easyestimateState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "AI scope generation from description", easyestimate: "No", easyestimateState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", easyestimate: "No", easyestimateState: "no", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "UK construction cost library depth", easyestimate: "Basic", easyestimateState: "partial", tpt: "519 items, 31 categories", tptState: "yes" },
  { feature: "Scope completeness validation", easyestimate: "No", easyestimateState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", easyestimate: "No", easyestimateState: "no", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Pricing Accuracy Index (learns from jobs)", easyestimate: "No", easyestimateState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Ease of use for first quote", easyestimate: "Very easy (simple tool)", easyestimateState: "yes", tpt: "Easy (under 10 minutes)", tptState: "yes" },
  { feature: "Suited to growing businesses", easyestimate: "Limited: sole-trader focus", easyestimateState: "partial", tpt: "Yes: scales with the business", tptState: "yes" },
  { feature: "Branded PDF quote output", easyestimate: "Basic", easyestimateState: "partial", tpt: "Yes: professional output", tptState: "yes" },
  { feature: "Free trial available", easyestimate: "Yes", easyestimateState: "yes", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Starting price", easyestimate: "Low cost entry tier", easyestimateState: "yes", tpt: "£29/month (per account)", tptState: "yes" },
  { feature: "Credit card required to trial", easyestimate: "Varies", easyestimateState: "partial", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "No AI and no drawing upload",
    desc: "EasyEstimate is designed for simplicity and manual entry. There is no AI that reads a project description and generates a complete scope, and no ability to upload structural drawings for automatic interpretation. For builders who want to quote faster with less manual input, this is a significant gap.",
  },
  {
    title: "Limited cost library depth",
    desc: "EasyEstimate's cost library is designed for simple jobs with a relatively small number of items. For builders quoting complex extensions, loft conversions, or structural alterations with steelwork, groundworks, and multiple trade categories, the library depth may not cover the full scope.",
  },
  {
    title: "Designed for sole traders, not growing businesses",
    desc: "EasyEstimate is marketed at builders who want the absolute simplest tool. As a business grows and jobs become more complex, the platform's simplicity becomes a limitation rather than a benefit. The Proper Tool is designed to scale: AI quoting, drawing analysis, and scope validation grow in value as job complexity increases.",
  },
  {
    title: "No scope completeness validation or Pricing Accuracy Index",
    desc: "EasyEstimate does not validate quote completeness or learn from completed jobs to improve future pricing accuracy. The Proper Tool's scope validation catches items that are commonly missed, and the Pricing Accuracy Index improves estimate accuracy based on your actual margins over time.",
  },
];

const STAY_ON_EASYESTIMATE = [
  "You are a sole trader doing simple, repetitive jobs where a basic item-by-item estimate is all you need.",
  "You have no interest in AI, drawing uploads, or scope validation and want the absolute simplest possible tool.",
  "The cost library depth of EasyEstimate is sufficient for the type of work you do.",
  "You are not planning to grow your business significantly and do not need the tool to scale with you.",
];

const SWITCH_REASONS = [
  "You want to generate quotes from drawings or a project description without manually entering every item.",
  "Your jobs are becoming more complex and you need a deeper UK cost library with more trade categories.",
  "You want scope completeness validation to catch items you may have missed and reduce underquoting.",
  "You want pricing that learns from your actual margins and improves over time.",
  "You want professional branded PDF output that presents your business more formally.",
  "You want a 14-day free trial with no credit card, so you can properly evaluate the tool on real jobs.",
];

const FAQS = [
  {
    q: "What is the best EasyEstimate alternative for UK builders?",
    a: "For UK builders who want AI-powered quoting, drawing upload, and a deeper UK cost library alongside simplicity, The Proper Tool is the strongest EasyEstimate alternative. EasyEstimate has no AI, no drawing upload, and a more limited cost library. The Proper Tool starts at £29/month with a 14-day free trial.",
  },
  {
    q: "Is EasyEstimate good enough for growing building businesses?",
    a: "EasyEstimate is designed for sole traders who want the simplest possible tool. For builders who are growing, taking on more complex jobs, or wanting to quote from drawings, EasyEstimate's limited feature set becomes a constraint. The Proper Tool grows with the business, adding AI scope generation, drawing analysis, scope validation, and a 519-item UK cost library.",
  },
  {
    q: "Can EasyEstimate read structural drawings or generate quotes from descriptions?",
    a: "No. EasyEstimate does not read architectural drawings or generate quotes from project descriptions. All items must be entered manually. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more complete quote.",
  },
  {
    q: "How does EasyEstimate pricing compare to The Proper Tool?",
    a: "EasyEstimate is positioned as a low-cost option for sole traders. The Proper Tool starts at £29 per month with a 14-day free trial and no credit card required. For the additional capability, including AI quoting, drawing upload, a 519-item UK cost library, and scope validation, The Proper Tool represents strong value.",
  },
  {
    q: "What is the UK cost library depth difference between EasyEstimate and The Proper Tool?",
    a: "EasyEstimate has a basic cost library for simple estimating. The Proper Tool has a 519-item UK cost library across 31 residential construction trade categories, including groundworks, structural steelwork, carpentry, roofing, and finishing trades. The Pricing Accuracy Index learns from your actual margins over time.",
  },
];

export default function EasyestimateAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="EasyEstimate Alternative for UK Builders: AI Quoting with a Deeper Cost Library | The Proper Tool"
        description="Looking for an EasyEstimate alternative with AI-powered quoting and a deeper UK cost library? The Proper Tool offers a 519-item UK library, drawing upload, and a 14-day free trial from £29/mo."
        canonical="/easyestimate-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>EasyEstimate Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              EasyEstimate Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              EasyEstimate is the simplest UK estimating tool for sole traders. If you want AI-powered quoting from drawings, a deeper UK cost library, and a tool that grows with your business, here is how The Proper Tool compares.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why builders look for an EasyEstimate alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EasyEstimate is genuinely easy to use and serves a specific need well. But builders who are growing or taking on more complex work consistently run into the same limitations:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs EasyEstimate: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">EasyEstimate</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.easyestimate} state={row.easyestimateState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
              Competitor pricing and feature information is based on publicly available data at time of writing. Prices and features may have changed. Verify directly with EasyEstimate before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EasyEstimate is positioned as a low-cost entry-level tool for sole traders. The Proper Tool starts at £29 per month with a 14-day free trial, and delivers significantly more capability: AI scope generation, drawing upload, a 519-item UK cost library, scope validation, and a Pricing Accuracy Index that learns from completed jobs.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">EasyEstimate</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Low-cost entry for sole traders</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No AI, no drawing upload, no scope validation</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Basic cost library, limited trade categories</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Does not scale well for growing or complex businesses</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial, no credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />519-item UK cost library, AI quoting, drawing upload</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Scales with the business as jobs get more complex</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on EasyEstimate</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EasyEstimate is the right tool for a specific type of builder. If the following describes your situation, it may still be the best choice:
            </p>
            <div className="space-y-3">
              {STAY_ON_EASYESTIMATE.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons builders switch from EasyEstimate to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically driven by job complexity growth, the desire for AI quoting, or needing a deeper cost library.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              UK-built quoting software for residential builders. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with EasyEstimate before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/pricejob-alternative", label: "PriceAJob Alternative" },
                { href: "/estimage-alternative", label: "esti-mate Alternative" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
